import type { Player } from "../../shared/types";
import type { Difficulty } from "./theme";
import { START, cleanRules, type Rules } from "./roomRules";

// Persist the Play Locally setup so leaving/finishing a match and coming back
// keeps your last roster + settings (play again with the same thing). Local only;
// online lobbies always start fresh.
export interface SavedLocalSetup {
  players: Player[];
  max: number;
  fill: Difficulty;
  mapValue: string;
  winMode: "score" | "rounds";
  score: number;
  rounds: number;
  disabledPowerUps: string[];
  spawnChance: number;
  wormSpeed: number;
}

const KEY = "ar.setup.local.v1";
const ARENA_MIGRATED_KEY = "ar.setup.local.arena2to1";

export function loadLocalSetup(): SavedLocalSetup | null {
  try {
    const raw = localStorage.getItem(KEY);
    const s = raw ? JSON.parse(raw) : null;
    // Once: a setup saved on the old default arena (Square 700, before the 2:1
    // arenas) moves to the new default, the Small Rectangle. A square picked
    // after this stays picked.
    if (s && !localStorage.getItem(ARENA_MIGRATED_KEY)) {
      if (s.mapValue === "Square 700") s.mapValue = "Rectangle 735 368";
      localStorage.setItem(ARENA_MIGRATED_KEY, "1");
    }
    return s && Array.isArray(s.players) ? s : null;
  } catch {
    return null;
  }
}

export function saveLocalSetup(s: SavedLocalSetup): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode / quota - ignore */
  }
}

// The local game's rules (the easy rules: flavour, map, length, options), kept
// next to the saved roster so "play again" starts from the same game.
const RULES_KEY = "ar.setup.local.rules.v1";

export function loadLocalRules(): Rules {
  try {
    const raw = localStorage.getItem(RULES_KEY);
    return raw ? cleanRules(JSON.parse(raw)) : { ...START };
  } catch {
    return { ...START };
  }
}

export function saveLocalRules(r: Rules): void {
  try {
    localStorage.setItem(RULES_KEY, JSON.stringify(r));
  } catch {
    /* private mode / quota - ignore */
  }
}
